/**
 * Pure planning for streamed export audio.
 *
 * The export mix is rendered in short, sample-aligned windows instead of one buffer for the whole
 * range, so memory is bounded by the window length (not the export length). Window boundaries are
 * integer sample positions; each window plays exactly the timeline span `[from, to)` it covers, so
 * consecutive windows join without gaps or overlaps.
 */
import type { AudioSegment } from '@ve/core';
import { segmentWindow, type SegmentWindow } from '../audio/schedule.ts';

export interface AudioWindow {
  index: number;
  /** Sample offsets relative to the export start: `[startSample, endSample)`. */
  startSample: number;
  endSample: number;
  /** Timeline seconds covered: `[from, to)`. */
  from: number;
  to: number;
}

/** Total samples of an export range (rounded to the nearest sample). */
export function totalSamples(startSec: number, endSec: number, sampleRate: number): number {
  return Math.max(0, Math.round((endSec - startSec) * sampleRate));
}

/** Splits `[startSec, endSec)` into consecutive windows of `windowSeconds` (last one shorter). */
export function planAudioWindows(
  startSec: number,
  endSec: number,
  sampleRate: number,
  windowSeconds: number,
): AudioWindow[] {
  const total = totalSamples(startSec, endSec, sampleRate);
  const size = Math.max(1, Math.round(windowSeconds * sampleRate));
  const windows: AudioWindow[] = [];
  for (let s = 0, index = 0; s < total; s += size, index++) {
    const e = Math.min(total, s + size);
    windows.push({
      index,
      startSample: s,
      endSample: e,
      from: startSec + s / sampleRate,
      to: startSec + e / sampleRate,
    });
  }
  return windows;
}

export interface WindowPart {
  segment: AudioSegment;
  /** What of the segment plays in the window (timeline and source coordinates). */
  play: SegmentWindow;
  /** Source span (seconds) to decode for this part. */
  sourceFrom: number;
  sourceTo: number;
}

/**
 * The segments audible in a window and the source span each needs. `play.when` is relative to the
 * window start (context time 0).
 */
export function partsForWindow(
  segments: readonly AudioSegment[],
  win: Pick<AudioWindow, 'from' | 'to'>,
): WindowPart[] {
  const parts: WindowPart[] = [];
  for (const segment of segments) {
    const play = segmentWindow(segment, { timelineTime: win.from, contextTime: 0, endTime: win.to });
    if (!play) continue;
    parts.push({ segment, play, sourceFrom: play.offset, sourceTo: play.offset + play.bufferDuration });
  }
  return parts;
}

/** For each media id, the index of the last window that needs it (to release decoders early). */
export function lastWindowByMedia(
  segments: readonly AudioSegment[],
  windows: readonly AudioWindow[],
): Map<string, number> {
  const last = new Map<string, number>();
  for (const win of windows) {
    for (const part of partsForWindow(segments, win)) last.set(part.segment.mediaId, win.index);
  }
  return last;
}

/** Samples per encoded packet for codecs with a fixed frame size. */
const SAMPLES_PER_PACKET: Record<string, number> = { aac: 1024 };

/**
 * Upper bound of audio packets the encoder will produce for `durationSec` of audio, used for
 * `fastStart: 'reserve'` (exceeding it is an error; overestimating only reserves a few bytes per
 * packet). AAC frames are 1024 samples; for other codecs (e.g. Opus, whose frame duration the
 * encoder may choose) mediabunny's guidance of "10 ms or 512 samples, whichever is shorter" is used.
 * A margin covers encoder priming/padding packets.
 */
export function estimateAudioPackets(durationSec: number, sampleRate: number, codec: string): number {
  const fixed = SAMPLES_PER_PACKET[codec];
  const packetSeconds = fixed ? fixed / sampleRate : Math.min(0.01, 512 / sampleRate);
  return Math.ceil((durationSec / packetSeconds) * 1.1) + 64;
}

/** Upper bound of video packets: one per frame, plus a small margin. */
export function estimateVideoPackets(frames: number): number {
  return frames + 8;
}
